/**
 * Finance Dashboard
 * Overview of church financial health with KPIs and quick actions
 */

import React, { useEffect, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  Plus,
  FileText,
  Calendar,
  Users,
  Wallet,
  PieChart,
  AlertCircle,
  CheckCircle2
} from 'lucide-react'
import { AdminUser } from '@/lib/authService'
import { ContributionService } from '@/services/contributionService'
import { ReportService } from '@/services/reportService'
import { useNavigate } from 'react-router-dom'
import { formatCurrency } from '@/lib/currency'
import { useSubscription } from '@/hooks/useSubscription'
import { FeatureRestricted } from '@/components/subscription/FeatureRestricted'

interface FinanceDashboardProps {
  user: AdminUser | null
}

export default function FinanceDashboard({ user }: FinanceDashboardProps) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [dashboardData, setDashboardData] = useState<any>(null)

  // Check subscription access
  const { features, limits, loading: subscriptionLoading } = useSubscription(user?.churchId)

  useEffect(() => {
    if (user?.churchId) {
      loadDashboardData()
    }
  }, [user])

  async function loadDashboardData() {
    try {
      setLoading(true)

      // Get current month dates
      const now = new Date()
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0)
      const startOfYear = new Date(now.getFullYear(), 0, 1)

      // Fetch data in parallel
      const [monthSummary, yearSummary, financialPosition] = await Promise.all([
        ContributionService.getChurchContributionSummary(
          user!.churchId,
          startOfMonth.toISOString().split('T')[0],
          endOfMonth.toISOString().split('T')[0]
        ),
        ContributionService.getChurchContributionSummary(
          user!.churchId,
          startOfYear.toISOString().split('T')[0],
          now.toISOString().split('T')[0]
        ),
        ReportService.getStatementOfFinancialPosition(
          user!.churchId,
          now.toISOString().split('T')[0]
        )
      ])

      setDashboardData({
        month: monthSummary,
        year: yearSummary,
        financialPosition,
        currentDate: now
      })
    } catch (error) {
      console.error('Error loading dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading || subscriptionLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="h-16 w-16 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  // Check if finance feature is enabled
  if (!features.finance) {
    return (
      <FeatureRestricted
        featureName="Finance Management"
        description="Manage contributions, expenses, budgets, and comprehensive financial reporting for your church."
        currentPlan={limits?.plan || 'starter'}
        requiredPlan="growth"
        icon={<DollarSign className="h-10 w-10 text-white" />}
      />
    )
  }

  const monthlyGiving = dashboardData?.month?.total_amount || 0
  const yearlyGiving = dashboardData?.year?.total_amount || 0
  const totalAssets = dashboardData?.financialPosition?.total_assets || 0
  const totalLiabilities = dashboardData?.financialPosition?.total_liabilities || 0
  const netAssets = dashboardData?.financialPosition?.total_net_assets || 0

  // Calculate month-over-month change (placeholder - would need previous month data)
  const monthlyChange = 8.2
  const yearlyChange = 12.5

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-background px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Finance Dashboard</h1>
            <p className="text-sm text-muted-foreground">
              Overview of your church's financial health
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate('/finance/contributions')}>
              <Plus className="mr-2 h-4 w-4" />
              Record Contribution
            </Button>
            <Button variant="outline" onClick={() => navigate('/finance/reports')}>
              <FileText className="mr-2 h-4 w-4" />
              View Reports
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6">
        {/* KPI Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
          {/* Monthly Giving */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Monthly Giving</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(monthlyGiving, 'GHS')}
              </div>
              <div className="flex items-center text-xs text-muted-foreground mt-1">
                {monthlyChange >= 0 ? (
                  <>
                    <ArrowUpRight className="h-3 w-3 text-green-500 mr-1" />
                    <span className="text-green-500">+{monthlyChange}%</span>
                  </>
                ) : (
                  <>
                    <ArrowDownRight className="h-3 w-3 text-red-500 mr-1" />
                    <span className="text-red-500">{monthlyChange}%</span>
                  </>
                )}
                <span className="ml-1">from last month</span>
              </div>
            </CardContent>
          </Card>

          {/* Yearly Giving */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Year to Date</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(yearlyGiving, 'GHS')}
              </div>
              <div className="flex items-center text-xs text-muted-foreground mt-1">
                <ArrowUpRight className="h-3 w-3 text-green-500 mr-1" />
                <span className="text-green-500">+{yearlyChange}%</span>
                <span className="ml-1">from last year</span>
              </div>
            </CardContent>
          </Card>

          {/* Total Assets */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Assets</CardTitle>
              <Wallet className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(totalAssets, 'GHS')}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Current financial position
              </p>
            </CardContent>
          </Card>

          {/* Net Assets */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Net Assets</CardTitle>
              <PieChart className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(netAssets, 'GHS')}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Assets - Liabilities
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 md:grid-cols-2 mb-6">
          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Latest financial transactions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {dashboardData?.month?.by_type && Object.entries(dashboardData.month.by_type).slice(0, 5).map(([type, data]: [string, any]) => (
                  <div key={type} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
                        <DollarSign className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium capitalize">{type.replace('_', ' ')}</p>
                        <p className="text-xs text-muted-foreground">{data.count} transactions</p>
                      </div>
                    </div>
                    <div className="text-sm font-medium">
                      {formatCurrency(data.amount, 'GHS')}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Top Contributors */}
          <Card>
            <CardHeader>
              <CardTitle>Top Contributors</CardTitle>
              <CardDescription>This month's leading givers</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {dashboardData?.month?.top_contributors?.slice(0, 5).map((contributor: any, index: number) => (
                  <div key={contributor.member_id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                        <span className="text-sm font-medium text-primary">#{index + 1}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium">{contributor.member_name}</p>
                        <p className="text-xs text-muted-foreground">Member</p>
                      </div>
                    </div>
                    <div className="text-sm font-medium">
                      {formatCurrency(contributor.total_amount, 'GHS')}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common financial tasks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
              <Button
                variant="outline"
                className="h-auto flex-col items-start p-4 gap-2"
                onClick={() => navigate('/finance/contributions')}
              >
                <DollarSign className="h-5 w-5 text-primary" />
                <div className="text-left">
                  <div className="font-medium">Record Contribution</div>
                  <div className="text-xs text-muted-foreground">Add tithes & offerings</div>
                </div>
              </Button>

              <Button
                variant="outline"
                className="h-auto flex-col items-start p-4 gap-2"
                onClick={() => navigate('/finance/expenses')}
              >
                <FileText className="h-5 w-5 text-primary" />
                <div className="text-left">
                  <div className="font-medium">Submit Expense</div>
                  <div className="text-xs text-muted-foreground">Track church expenses</div>
                </div>
              </Button>

              <Button
                variant="outline"
                className="h-auto flex-col items-start p-4 gap-2"
                onClick={() => navigate('/finance/reports')}
              >
                <PieChart className="h-5 w-5 text-primary" />
                <div className="text-left">
                  <div className="font-medium">Financial Reports</div>
                  <div className="text-xs text-muted-foreground">View statements</div>
                </div>
              </Button>

              <Button
                variant="outline"
                className="h-auto flex-col items-start p-4 gap-2"
                onClick={() => navigate('/finance/budgets')}
              >
                <Calendar className="h-5 w-5 text-primary" />
                <div className="text-left">
                  <div className="font-medium">Budget vs Actual</div>
                  <div className="text-xs text-muted-foreground">Track spending</div>
                </div>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Alerts & Notifications */}
        {(dashboardData?.alerts?.length > 0) && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Alerts & Notifications</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-start gap-3 rounded-lg border p-3">
                  <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Budget Alert</p>
                    <p className="text-xs text-muted-foreground">Utilities expense is at 85% of budget</p>
                  </div>
                  <Button variant="ghost" size="sm">Review</Button>
                </div>

                <div className="flex items-start gap-3 rounded-lg border p-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Bank Reconciliation</p>
                    <p className="text-xs text-muted-foreground">Last reconciled 2 days ago</p>
                  </div>
                  <Button variant="ghost" size="sm">Reconcile</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
